import dayjs from 'dayjs'

import {
  CAMPAIGN_BLOCKED_SHORTENER_DOMAINS,
  CAMPAIGN_BUSINESS_NAME_START_CHARACTERS,
  CAMPAIGN_URGENCY_MARKETING_PHRASES,
} from '~/features/campaigns/components/campaignBuilder/constants'
import {
  ECampaignScheduleStepKeys,
  EScheduleStepOffsetUnit,
  EStructuredTimingMode,
  TCampaignScheduleStepForm,
} from '~/features/campaigns/models'
import { OUTREACH_TIME_OPTIONS } from '~/features/campaigns/utils/constants'
import { escapeRegExp } from '~/utils/regExp'

const HTTP_URL_IN_TEXT_REGEX = /https?:\/\/[^\s<>"']+/gi

function trimUrlToken(text: string): string {
  return text.replace(/^[([<'"]+/, '').replace(/[.,;:!?)'"\]>]+$/, '')
}

function tryParseUrlFromRaw(raw: string): URL | null {
  const trimmed = trimUrlToken(raw)
  if (!trimmed) return null
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed)
    }
    if (/^[a-z0-9]/i.test(trimmed) && /\.[a-z]{2,}([/?#]|$)/i.test(trimmed)) {
      return new URL(`https://${trimmed}`)
    }
  } catch {
    return null
  }
  return null
}

function collectHostnamesFromMessage(message: string): Set<string> {
  const hostnames = new Set<string>()
  const add = (raw: string) => {
    const parsed = tryParseUrlFromRaw(raw)
    if (parsed?.hostname) hostnames.add(parsed.hostname.toLowerCase())
  }

  const httpMatches = message.match(HTTP_URL_IN_TEXT_REGEX) ?? []
  httpMatches.forEach((fragment) => add(fragment))

  message.split(/\s+/).forEach((token) => {
    if (!/^https?:\/\//i.test(token)) {
      add(token)
    }
  })

  return hostnames
}

function hostnameMatchesBlockedDomain(
  hostname: string,
  blockedDomain: string,
): boolean {
  const h = hostname.toLowerCase()
  const d = blockedDomain.toLowerCase()
  if (!d) return false
  return h === d || h.endsWith(`.${d}`)
}

export const messageContainsAnyBlockedShortener = (
  message: string,
): boolean => {
  if (!message || CAMPAIGN_BLOCKED_SHORTENER_DOMAINS.length === 0) return false

  const hostnames = collectHostnamesFromMessage(message)

  return [...hostnames].some((hostname) =>
    CAMPAIGN_BLOCKED_SHORTENER_DOMAINS.some((domain) =>
      hostnameMatchesBlockedDomain(hostname, domain),
    ),
  )
}

export const messageStartContainsBusinessName = (
  message: string,
  rawBusinessName: string,
  options?: {
    businessNameMergeFields?: string[]
  },
): boolean => {
  const messageStart = message.slice(0, CAMPAIGN_BUSINESS_NAME_START_CHARACTERS)
  const normalizedMessage = messageStart.toLowerCase()
  const businessNameMergeFields =
    options?.businessNameMergeFields?.filter(Boolean) ?? []

  const hasBusinessNameMergeField = businessNameMergeFields.some((field) => {
    const normalizedField = field.trim().toLowerCase()
    if (!normalizedField) return false

    return (
      normalizedMessage.includes(`{{${normalizedField}}}`) ||
      normalizedMessage.includes(`[[${normalizedField}]]`)
    )
  })

  if (hasBusinessNameMergeField) return true

  const businessName = rawBusinessName
    .trim()
    .slice(0, CAMPAIGN_BUSINESS_NAME_START_CHARACTERS)
  if (!businessName) return false

  const normalizedName = businessName.toLowerCase()
  const words = normalizedName.split(/\s+/).filter(Boolean)

  if (words.length === 0) return false

  const escapedWords = words.map(escapeRegExp)
  const body = words.length === 1 ? escapedWords[0] : escapedWords.join('\\s+')

  return new RegExp(`\\b${body}\\b`, 'i').test(normalizedMessage)
}

const urgencyMarketingPhraseRegex = (phrase: string): RegExp => {
  const tokens = phrase.trim().split(/\s+/).map(escapeRegExp)
  const body = tokens.join('\\s+')
  return new RegExp(`\\b${body}\\b`)
}

export const messageContainsUrgencyMarketingPhrase = (
  message: string,
  phrase: string,
): boolean => {
  if (!message) return false
  return urgencyMarketingPhraseRegex(phrase).test(message)
}

export const messageContainsAnyUrgencyMarketingPhrase = (
  message: string,
): boolean =>
  CAMPAIGN_URGENCY_MARKETING_PHRASES.some((phrase) =>
    messageContainsUrgencyMarketingPhrase(message, phrase),
  )

/** Form string → API `number | null` (minutes since midnight). Empty → `null`. */
export function scheduleStepSendTimeToPayload(
  sendTime?: string | null,
): number | null {
  if (sendTime === null || sendTime === undefined || sendTime === '') {
    return null
  }

  const [hours, minutes] = sendTime.split(':').map((part) => parseInt(part, 10))

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null
  }

  return hours * 60 + minutes
}

/** API `number | null` → form string select value (`null` when unset). */
export function scheduleStepSendTimeFromPayload(
  sendTime?: number | null,
): string | null {
  if (sendTime === null || sendTime === undefined) {
    return null
  }

  const hours = Math.floor(sendTime / 60)
  const minutes = sendTime % 60
  const timeString = `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`

  return (
    OUTREACH_TIME_OPTIONS.find((option) => option.value === timeString)
      ?.value || timeString
  )
}

function getSendTimeLabel(sendTime?: string | null) {
  if (!sendTime) return null

  return (
    OUTREACH_TIME_OPTIONS.find((option) => option.value === sendTime)?.label ||
    sendTime
  )
}

export function getScheduleStepTimingLabel({
  index,
  step,
  timingMode,
}: {
  index: number
  step: TCampaignScheduleStepForm
  timingMode: EStructuredTimingMode
}) {
  const sendTime = step[ECampaignScheduleStepKeys.sendTime]
  const sendTimeLabel = getSendTimeLabel(sendTime)

  if (timingMode === EStructuredTimingMode.absolute) {
    const scheduleDate = step[ECampaignScheduleStepKeys.scheduleDate]
    const dateLabel = scheduleDate
      ? dayjs(scheduleDate).format('MMM D')
      : 'No date'

    return sendTimeLabel ? `${dateLabel} · ${sendTimeLabel}` : dateLabel
  }

  const offsetAmount = Number(step[ECampaignScheduleStepKeys.offsetAmount])
  const offsetUnit =
    step[ECampaignScheduleStepKeys.offsetUnit] || EScheduleStepOffsetUnit.days
  const isFirstStep = index === 0
  const anchor = isFirstStep ? 'after enrollment' : `after step ${index}`

  if (
    step[ECampaignScheduleStepKeys.offsetAmount] === null ||
    step[ECampaignScheduleStepKeys.offsetAmount] === undefined ||
    Number.isNaN(offsetAmount)
  ) {
    return 'Timing not set'
  }

  let base: string
  if (offsetAmount === 0) {
    base = isFirstStep ? 'On enrollment' : `Right after step ${index}`
  } else {
    base = `${offsetAmount} ${offsetUnit} ${anchor}`
  }

  return sendTimeLabel ? `${base} · ${sendTimeLabel}` : base
}
